<ng-content></ng-content>

<ng-template #filteredBadge let-typeLabel>
    <span
        class="cm-filter-badge"
        [matTooltip]="'Showing only ' + typeLabel + ' related to the current selection. Click to clear.'"
        (click)="$filterBySelection.set(false)"
    >
        <i class="far fa-filter"></i>
        <span class="cm-filter-label">Filtered</span>
        <i class="far fa-times cm-filter-clear"></i>
    </span>
</ng-template>

<ng-template #slotInputsContent>
    @if ($slotInputs().length > 0) {
        <div class="cm-template-settings">
            @for (input of $slotInputs(); track input.key) {
                <cm-value-slot
                    [node]="$parameters().parameters"
                    [key]="input.key"
                    [label]="input.label"
                    [icon]="input.icon"
                    [schema]="input.schema"
                    [selectionPossibilities]="input.selectionPossibilities"
                    [topLabel]="true"
                    [overwrittenValue]="input.overwrittenValue"
                    [showOverwrittenValueWarning]="true"
                    [changeSideEffect]="onChangedInputSideEffect(input.key)"
                    (updatedOverwrittenValue)="updateOverwrittenValue(input.key, $event)"
                    (mouseenter)="onMouseEnter(input)"
                    (mouseleave)="onMouseLeave()"
                >
                </cm-value-slot>
            }
        </div>
    }
</ng-template>

<ng-template #nonSlotInputsContent>
    @if ($nonSlotInputs().length > 0) {
        <div class="cm-template-settings">
            @for (input of $nonSlotInputs(); track input.key) {
                <cm-value-slot
                    [node]="$parameters().parameters"
                    [key]="input.key"
                    [label]="input.label"
                    [icon]="input.icon"
                    [schema]="input.schema"
                    [selectionPossibilities]="input.selectionPossibilities"
                    [topLabel]="true"
                    [overwrittenValue]="input.overwrittenValue"
                    [showOverwrittenValueWarning]="true"
                    [throttledUpdate]="input.throttledUpdate ?? false"
                    [min]="input.min"
                    [max]="input.max"
                    [delta]="input.delta"
                    [numberModifier]="input.numberModifier"
                    [changeSideEffect]="onChangedInputSideEffect(input.key)"
                    (updatedOverwrittenValue)="updateOverwrittenValue(input.key, $event)"
                    (mouseenter)="onMouseEnter(input)"
                    (mouseleave)="onMouseLeave()"
                >
                    <ng-container cm-top-right>
                        @if (input.specialType === "material") {
                            @if ($parameters().parameters.parameters[input.key]; as material) {
                                <div matTooltip="Move material" (click)="$event.stopPropagation()" [matMenuTriggerFor]="meshUVMenu" class="cm-icon">
                                    <i class="far fa-arrows"></i>
                                    <!-- hasBackdrop needs to be false in order for node drag and drop to work! -->
                                    <mat-menu [hasBackdrop]="false" #meshUVMenu>
                                        <div (click)="$event.stopPropagation()" class="cm-overlay-settings">
                                            <cm-inspector-section>
                                                <ng-container cm-title> Material Settings </ng-container>
                                                <ng-container cm-content>
                                                    @for (parameter of input.subParameters; track parameter.key) {
                                                        <cm-value-slot
                                                            [node]="$parameters().parameters"
                                                            [key]="parameter.key"
                                                            [label]="parameter.label"
                                                            [icon]="parameter.icon"
                                                            [schema]="parameter.schema"
                                                            [selectionPossibilities]="parameter.selectionPossibilities"
                                                            [overwrittenValue]="parameter.overwrittenValue"
                                                            [showOverwrittenValueWarning]="true"
                                                            [throttledUpdate]="parameter.throttledUpdate ?? false"
                                                            [min]="parameter.min"
                                                            [max]="parameter.max"
                                                            [delta]="parameter.delta"
                                                            [numberModifier]="parameter.numberModifier"
                                                            (updatedOverwrittenValue)="updateOverwrittenValue(parameter.key, $event)"
                                                        >
                                                        </cm-value-slot>
                                                    }
                                                </ng-container>
                                            </cm-inspector-section>
                                        </div>
                                    </mat-menu>
                                </div>
                            }
                        }
                    </ng-container>
                </cm-value-slot>
            }
        </div>
    }
</ng-template>

<cm-inspector-section>
    <ng-container cm-title> Template Instance </ng-container>
    <ng-container cm-content>
        <cm-template-thumbnail [template]="$node().parameters.template"></cm-template-thumbnail>
        <cm-value-slot [node]="$node()" [key]="'id'" [label]="'ID'" [validate]="$isValidExternalId()"></cm-value-slot>
        <cm-value-slot [node]="$node()" [key]="'name'" [label]="'Name'"></cm-value-slot>
        <cm-value-slot [node]="$node()" [key]="'template'" [label]="'Template'"></cm-value-slot>
    </ng-container>
</cm-inspector-section>

@if ($isActive()) {
    <cm-inspector-section>
        <ng-container cm-title>
            <span class="cm-section-title">
                <span>Inputs</span>
                @if ($inputsFiltered()) {
                    <ng-container *ngTemplateOutlet="filteredBadge; context: {$implicit: 'inputs'}"></ng-container>
                }
            </span>
        </ng-container>
        <ng-container cm-content>
            @if ($inputs().length === 0) {
                No inputs
            } @else {
                @if ($sectionedVariantInputs().length > 0) {
                    @for (input of $sectionedVariantInputs(); track input.title) {
                        <cm-inspector-section>
                            <ng-container cm-title> {{ input.title }} </ng-container>
                            <ng-container cm-content>
                                <div class="cm-template-settings">
                                    @for (sectionInput of input.configs; track sectionInput.key) {
                                        <cm-value-slot
                                            [node]="$parameters().parameters"
                                            [key]="sectionInput.key"
                                            [label]="sectionInput.label"
                                            [icon]="sectionInput.icon"
                                            [schema]="sectionInput.schema"
                                            [selectionPossibilities]="sectionInput.selectionPossibilities"
                                            [topLabel]="true"
                                            [overwrittenValue]="sectionInput.overwrittenValue"
                                            [showOverwrittenValueWarning]="true"
                                            [changeSideEffect]="onChangedInputSideEffect(sectionInput.key)"
                                            (updatedOverwrittenValue)="updateOverwrittenValue(sectionInput.key, $event)"
                                            (mouseenter)="onMouseEnter(sectionInput)"
                                            (mouseleave)="onMouseLeave()"
                                        >
                                        </cm-value-slot>
                                    }
                                </div>
                            </ng-container>
                        </cm-inspector-section>
                    }

                    @if ($slotInputs().length + $nonSlotInputs().length > 0) {
                        <cm-inspector-section>
                            <ng-container cm-title> Others </ng-container>
                            <ng-container cm-content>
                                <ng-container *ngTemplateOutlet="slotInputsContent"></ng-container>
                                <ng-container *ngTemplateOutlet="nonSlotInputsContent"></ng-container>
                            </ng-container>
                        </cm-inspector-section>
                    }
                } @else {
                    <ng-container *ngTemplateOutlet="slotInputsContent"></ng-container>
                    <ng-container *ngTemplateOutlet="nonSlotInputsContent"></ng-container>
                }
            }
        </ng-container>
    </cm-inspector-section>

    @if ($unusedInputs(); as unusedInputs) {
        @if (unusedInputs.length > 0) {
            <cm-inspector-section [collapsible]="true">
                <ng-container cm-title> Detached Inputs <i class="far fa-diamond-exclamation"></i></ng-container>
                <ng-container cm-content>
                    <div class="cm-template-settings">
                        @for (unusedInput of unusedInputs; track unusedInput.key) {
                            <cm-value-slot
                                [node]="$parameters().parameters"
                                [label]="unusedInput.label"
                                [key]="unusedInput.key"
                                [schema]="unusedInput.schema"
                                [topLabel]="true"
                                [changeSideEffect]="onChangedInputSideEffect(unusedInput.key)"
                            >
                            </cm-value-slot>
                        }
                    </div>
                </ng-container>
            </cm-inspector-section>
        }
    }

    <cm-inspector-section>
        <ng-container cm-title>
            <span class="cm-section-title">
                <span>Outputs</span>
                @if ($outputsFiltered()) {
                    <ng-container *ngTemplateOutlet="filteredBadge; context: {$implicit: 'outputs'}"></ng-container>
                }
            </span>
        </ng-container>
        <ng-container cm-content>
            @for (output of $outputs(); track output.props.id) {
                <cm-template-instance-output
                    [templateInstance]="$node()"
                    [descriptor]="output"
                    (mouseenter)="onMouseEnter(output.props)"
                    (mouseleave)="onMouseLeave()"
                ></cm-template-instance-output>
            }
            @if ($outputs().length === 0) {
                No outputs
            }
        </ng-container>
    </cm-inspector-section>
} @else {
    <div class="cm-inactive-notice">
        <i class="far fa-diamond-exclamation"></i>
        <span>Instance is currently not active</span>
    </div>
    @if ($unusedInputs(); as unusedInputs) {
        @if (unusedInputs.length > 0) {
            <cm-inspector-section>
                <ng-container cm-title> Inputs </ng-container>
                <ng-container cm-content>
                    <div class="cm-template-settings">
                        @for (unusedInput of unusedInputs; track unusedInput.key) {
                            <cm-value-slot
                                [node]="$parameters().parameters"
                                [label]="unusedInput.label"
                                [key]="unusedInput.key"
                                [schema]="unusedInput.schema"
                                [topLabel]="true"
                                [changeSideEffect]="onChangedInputSideEffect(unusedInput.key)"
                            >
                            </cm-value-slot>
                        }
                    </div>
                </ng-container>
            </cm-inspector-section>
        }
    }
}
